import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { Product } from '@interfaces/models/product';
import sanitizer from '@helpers/sanitizer';
import { Product as ProductSchemaType, ImageObject as ImageSchemaType } from 'schema-dts';
import { User } from '@interfaces/models/user';
import UserBadge from '@enums/userBadge';
import { ContentsPageMetadata } from '@interfaces/models/contentsPageMetadata';
import { ImagesService } from '@services/image-service';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { getGoogleShoppingCondition } from './schema-utils';

type ProductSchemaProps = {
  product: Product;
  seoMetaData?: ContentsPageMetadata;
};

const getSeller = (seller: User, origin: string) => {
  const sellerUrl = origin && seller?.id ? `${origin}/profile/${seller.id}` : undefined;
  return {
    '@type': seller?.badges?.includes('pro' as UserBadge) ? 'Organization' : 'Person',
    name: seller?.firstname,
    ...(sellerUrl && { '@id': sellerUrl, url: sellerUrl }),
  };
};

const MEASUREMENTS_TO_INCLUDE = ['Height', 'Width', 'Depth'];

const getOptionalMeasurements = (product: Product) => {
  return Object.fromEntries(
    MEASUREMENTS_TO_INCLUDE.map((optionalMeasurement) => {
      const productMeasurement = product?.measurements?.find((measurement) => measurement.name === optionalMeasurement);
      if (!productMeasurement) {
        return [null, null];
      }
      return [
        [optionalMeasurement],
        {
          '@type': 'QuantitativeValue',
          value: productMeasurement.value,
          ...(!!product?.unit && {
            unitCode: product?.unit,
          }),
        },
      ];
    }).filter((item) => item[0] !== null),
  );
};

const createImageSchema = (product: Product): ImageSchemaType => ({
  '@type': 'ImageObject',
  contentUrl: ImagesService.buildUrl(product?.pictures?.[0]?.path, { w: 1024, h: 1024 }, true),
  description: product?.name,
  width: '1024',
  height: '1024',
});

const getDescription = (description: string, seoDesc: string) =>
  description?.length < 50 ? seoDesc || description : description;

// Snap to start of UTC day so SSR and client hydration always produce the same
// YYYY-MM-DD string regardless of sub-second skew between the two renders.
const getPriceValidUntil = (): string => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + 60);
  return date.toISOString().split('T')[0];
};

const getPriceSpecification = (product: Product) => {
  const regularCents = product?.regularPrice?.cents;
  const currentCents = product?.price?.cents;
  if (!regularCents || !currentCents || regularCents <= currentCents) {
    return {};
  }
  return {
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      priceType: 'https://schema.org/StrikethroughPrice',
      price: (regularCents / 100).toString(),
      priceCurrency: product?.price?.currency ?? '',
    },
  };
};

const getAdditionalProperties = (product: Product) => {
  const properties: Array<{ '@type': string; name: string; value: string }> = [];

  if (product?.condition?.description) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'conditionGrade',
      value: product.condition.description,
    });
  }

  if (product?.receipt) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'authenticatedBy',
      value: 'Vestiaire Collective',
    });
  }

  const accessories = product?.packagings?.filter((p) => p.value && p.name).map((p) => p.name);
  if (accessories?.length) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'includes',
      value: accessories.join(', '),
    });
  }

  return properties.length > 0 ? { additionalProperty: properties } : {};
};

const getKeywords = (product: Product): string | undefined => {
  const parts = [
    product?.brand?.name,
    product?.category?.localizedName,
    product?.color?.localizedName,
    product?.material?.localizedName,
    product?.condition?.description,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : undefined;
};

const createProductSchema = (product: Product, seoMetaData?: ContentsPageMetadata, origin = ''): ProductSchemaType => {
  const keywords = getKeywords(product);
  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    ...(origin && product?.path && { '@id': `${origin}${product.path}` }),
    name: product?.name,
    sku: product?.id,
    ...(product?.model?.name && { model: product.model.name }),
    image: createImageSchema(product),
    description:
      getDescription(product?.localizedDescription, seoMetaData?.description) ??
      getDescription(product?.description, seoMetaData?.description),
    ...(product?.category && {
      category: product?.category?.localizedName,
    }),
    ...(product?.color && {
      color: product?.color?.localizedName,
    }),
    ...(product?.material && {
      material: product?.material?.localizedName,
    }),
    brand: {
      '@type': 'Brand',
      name: product?.brand?.name,
    },
    ...(keywords && { keywords }),
    offers: {
      '@type': 'Offer',
      url: origin ? `${origin}${product.path}` : product.path,
      priceCurrency: product?.price?.currency ?? '',
      price: ((product?.price?.cents ?? 0) / 100).toString(),
      priceValidUntil: getPriceValidUntil(),
      availability: product?.sold ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      itemCondition: getGoogleShoppingCondition(product?.condition?.id),
      ...getPriceSpecification(product),
      ...(!!product?.seller && {
        seller: getSeller(product?.seller, origin),
      }),
    },
    ...getOptionalMeasurements(product),
    ...getAdditionalProperties(product),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: 4.5,
      reviewCount: 44885,
    },
  };
};

const ProductSchema: React.FC<ProductSchemaProps> = (props) => {
  const { product, seoMetaData } = props;
  const { locale } = useRouter();
  const origin = getSeoBaseUrlFromLocale(locale);

  const [schema] = useState(() => createProductSchema(product, seoMetaData, origin));

  return (
    <script
      data-cy={`product-schema-${product.id}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: sanitizer(JSON.stringify(schema)),
      }}
    />
  );
};

export default ProductSchema;
